import { ArrowRight, BarChart3, Sparkles, Target } from "lucide-react";
import {
  analyticsSummary,
  attemptsToday,
  dailyGoal,
  dailyQuests,
  recommendNext,
  weaknessList,
} from "../lib/learningEngine";

// Neuer Lernblock pro Tag (didaktisch begruendete Groesse, kein Grind).
const NEW_BLOCK_SIZE = 8;
const DAILY_CAP_MINUTES = 30;
import { ProgressRing } from "./ProgressRing";
import { MasterySheet } from "./MasterySheet";
import { StreakHeatmap } from "./StreakHeatmap";
import type { LearningExercise, LearningPath, LearningPathId, LearningState } from "../types";

// Dashboard, ausgelagert aus der Uebungsansicht (Layout C, Spec §2): alle Kennzahlen,
// Meisterung, Quests und Schwachstellen liegen hier, damit das Trainieren ruhig bleibt.
export function OverviewPanel({
  state,
  exercises,
  paths,
  onStartLearning,
}: {
  state: LearningState;
  exercises: LearningExercise[];
  paths: LearningPath[];
  onStartLearning?: (pathId: LearningPathId) => void;
}) {
  const summary = analyticsSummary(state, exercises);
  const quests = dailyQuests(state, exercises);
  const weaknesses = weaknessList(state, exercises);
  const goal = dailyGoal({ dueCount: summary.due, newBlockSize: NEW_BLOCK_SIZE, capMinutes: DAILY_CAP_MINUTES });
  const doneToday = attemptsToday(state);
  const nextUp = recommendNext(state, exercises, paths);

  return (
    <section className="overview-panel">
      <div className="panel-heading">
        <span className="eyebrow">Übersicht</span>
        <h2>Fortschritt, Fälligkeit und Schwachstellen auf einen Blick.</h2>
      </div>

      <section className="daily-goal" aria-label="Tagesziel">
        <div className="daily-goal-head">
          <span className="eyebrow">
            <Target size={15} /> Tagesziel heute
          </span>
          <strong>
            {Math.min(doneToday, goal.targetItems)}/{goal.targetItems} Aufgaben
          </strong>
        </div>
        <meter max={goal.targetItems} value={Math.min(doneToday, goal.targetItems)} />
        <p>
          {summary.due} fällig + {NEW_BLOCK_SIZE} neu · Richtwert max. {goal.capMinutes} min Fokuszeit. Lieber
          täglich eine gute Session als seltener Marathon.
        </p>
        {nextUp && onStartLearning ? (
          <button
            className="primary-action daily-goal-cta"
            type="button"
            onClick={() => onStartLearning(nextUp.pathId as LearningPathId)}
          >
            Jetzt lernen: {nextUp.label}
            {nextUp.due > 0 ? ` · ${nextUp.due} fällig` : ""}
            <ArrowRight size={17} />
          </button>
        ) : null}
      </section>

      <section className="command-strip" aria-label="Kennzahlen">
        <ProgressRing
          caption="Fokuszeit"
          label={`${summary.goodLearningMinutes}m`}
          value={Math.min(100, (summary.goodLearningMinutes / 45) * 100)}
        />
        <div className="metric-card">
          <span>Trefferquote</span>
          <strong>{summary.accuracy}%</strong>
          <small>{summary.totalAttempts} Antworten</small>
        </div>
        <div className="metric-card">
          <span>Faellig</span>
          <strong>{summary.due}</strong>
          <small>{summary.stable} pruefungsstabil</small>
        </div>
        <div className="metric-card">
          <span>Aktivitaet</span>
          <strong>{summary.streak} Tage aktiv</strong>
          <small>{state.recoveryDays} Erholungstage</small>
          <StreakHeatmap state={state} />
        </div>
      </section>

      <div className="overview-grid">
        <section className="side-panel">
          <div className="side-title">
            <Sparkles size={18} />
            Tages-Contracts
          </div>
          {quests.map((quest) => (
            <div className={`quest-row ${!quest.done && quest.progress >= 75 ? "is-close" : ""}`} key={quest.id}>
              <div>
                <strong>{quest.label.replace("Good Learning Time", "Fokuszeit")}</strong>
                <span>{quest.done ? "erledigt" : `${quest.progress}%`}</span>
              </div>
              <meter max="100" value={quest.progress} />
            </div>
          ))}
        </section>

        <section className="side-panel">
          <div className="side-title">
            <BarChart3 size={18} />
            Schwachstellen
          </div>
          {weaknesses.length ? (
            weaknesses.map((weakness) => (
              <div className="weak-row" key={weakness.topic}>
                <strong>{weakness.topic}</strong>
                <span>
                  {weakness.wrong}/{weakness.total} Fehler · {weakness.tag}
                </span>
              </div>
            ))
          ) : (
            <p>Noch keine belastbaren Fehlerdaten.</p>
          )}
        </section>
      </div>

      <MasterySheet state={state} exercises={exercises} paths={paths} />
    </section>
  );
}
